class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.stylesReady = null;
    this.overlay = null;
    this._boundClose = this.close.bind(this);
    this._boundHandleDocumentKeyDown = this._handleDocumentKeyDown.bind(this);
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    this.deferredObserver = new MutationObserver(() => {
      if (this.classList.contains('active')) this.hydrateDeferredContent();
    });
    this.deferredObserver.observe(this, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    this.deferredObserver?.disconnect();
  }

  hydrateDeferredContent() {
    this.querySelectorAll('template[data-drawer-deferred]').forEach(template => {
      template.replaceWith(template.content);
    });
  }

  hydrateDrawerContent() {
    const container = this.querySelector('#CartDrawer');
    const template = this.querySelector('template[data-cart-drawer-content]');

    if (container && template) {
      container.appendChild(template.content);
      template.remove();
    }

    this.bindOverlay();
    this.initializeDynamicCheckout();
  }

  bindOverlay() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (!overlay || overlay === this.overlay) return;

    this.overlay?.removeEventListener('click', this._boundClose);
    this.overlay = overlay;
    this.overlay.addEventListener('click', this._boundClose);
  }

  renderDrawerSection(html) {
    const container = this.querySelector('#CartDrawer');
    if (!container || !html) return;

    const responseDocument = new DOMParser().parseFromString(html, 'text/html');
    const responseTemplate = responseDocument.querySelector('template[data-cart-drawer-content]');
    if (!responseTemplate) return;

    container.replaceChildren(responseTemplate.content.cloneNode(true));
    this.querySelector('template[data-cart-drawer-content]')?.remove();
    this.bindOverlay();
    this.initializeDynamicCheckout();
  }

  initializeDynamicCheckout() {
    if (!this.querySelector('.additional-checkout-buttons')) return;

    requestAnimationFrame(() => {
      window.Shopify?.PaymentButton?.init?.();
    });
  }

  loadStyles() {
    if (this.stylesReady) return this.stylesReady;

    const template = this.querySelector('template[data-cart-drawer-styles]');
    if (!template) {
      this.setAttribute('data-cart-drawer-styles-ready', '');
      this.stylesReady = Promise.resolve();
      return this.stylesReady;
    }

    const links = [...template.content.querySelectorAll('link[rel="stylesheet"]')];
    this.stylesReady = Promise.all(links.map((sourceLink) => {
      const href = new URL(sourceLink.dataset.href, document.baseURI).href;
      const existingLink = [...document.querySelectorAll('link[rel="stylesheet"]')]
        .find((link) => link.href === href);

      if (existingLink?.sheet) return Promise.resolve();

      return new Promise((resolve) => {
        const link = existingLink || sourceLink.cloneNode(true);
        link.href = href;
        link.removeAttribute('data-href');
        link.setAttribute('data-cart-drawer-stylesheet', '');
        link.addEventListener('load', resolve, { once: true });
        link.addEventListener('error', resolve, { once: true });
        if (!existingLink) document.head.appendChild(link);
      });
    })).then(() => {
      template.remove();
      this.setAttribute('data-cart-drawer-styles-ready', '');
    });

    return this.stylesReady;
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('pointerenter', () => this.loadStyles(), { once: true, passive: true });
    cartLink.addEventListener('focus', () => this.loadStyles(), { once: true });
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  async open(triggeredBy) {
    await this.loadStyles();
    this.hydrateDrawerContent();
    this.hydrateDeferredContent();
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    setTimeout(() => {
      this.classList.add('animate', 'active');
    }, 30);

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        if (typeof trapFocus === 'function') {
            trapFocus(containerToTrapFocusOn, focusElement);
        }
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    document.addEventListener('keydown', this._boundHandleDocumentKeyDown);
  }

  close() {
    if (!this.classList.contains('active')) return;

    this.classList.remove('active');
    if (typeof removeTrapFocus === 'function' && this.activeElement) {
        removeTrapFocus(this.activeElement);
    }
    document.body.classList.remove('overflow-hidden');
    document.removeEventListener('keydown', this._boundHandleDocumentKeyDown);
  }

  _handleDocumentKeyDown(event) {
    if (event.code === 'Escape') {
      if (this.classList.contains('active')) {
        const isModalDrawerOpen = document.querySelector('modal-drawer[open]');
        if (isModalDrawerOpen) {
          return;
        }
        event.preventDefault();
        this.close();
      }
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    if (typeof onKeyUpEscape === 'function') {
        cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
    }
  }

  renderContents(parsedState) {
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner && drawerInner.classList.contains('is-empty')) {
        drawerInner.classList.remove('is-empty');
    }
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (sectionElement) {
        if (section.id === 'cart-drawer') {
          this.renderDrawerSection(parsedState.sections[section.id]);
        } else {
          sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
        }
      }
    });

    setTimeout(() => {
      this.open();
    }, 50);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
